import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid,
} from "recharts";
import {
  PaperPlaneTilt,
  EnvelopeOpen,
  CursorClick,
  ChatCircle,
  Lightning,
  Spinner,
  ArrowClockwise,
  Circle,
  Pause,
  Play,
  Phone,
  UsersThree,
  Broadcast,
  WarningCircle,
  Trash,
  Download,
  Briefcase,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AnalyticsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [view, setView] = useState("instantly"); // "instantly" | "voice_drop"
  const [loading, setLoading] = useState(true);
  const [emailData, setEmailData] = useState(null);
  const [voiceData, setVoiceData] = useState(null);
  const [downloadingWf, setDownloadingWf] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [syncingCallbacks, setSyncingCallbacks] = useState(false);
  const [deletingWf, setDeletingWf] = useState(null);

  // Remove a workflow's Dolphin LeadFlow history (pushes / callbacks / config).
  // The workflow inside GHL is NEVER touched — we don't have API rights and
  // this is really just a "clear the analytics row" action for orphaned or
  // test workflows the user no longer wants to see.
  const deleteWorkflowHistory = async (wf) => {
    if (!wf?.workflow_id) return;
    const label = wf.workflow_name || wf.workflow_id;
    const ok = window.confirm(
      `Remove "${label}" from the Voice Drop Workflows table?\n\n` +
        "This clears its push history, callback counts, and saved defaults in Dolphin LeadFlow only. " +
        "The workflow itself in GHL will NOT be touched.",
    );
    if (!ok) return;
    setDeletingWf(wf.workflow_id);
    try {
      const res = await axios.delete(
        `${API}/integrations/voice-drop/workflows/${wf.workflow_id}`,
        { headers: getAuthHeaders() },
      );
      const d = res.data?.deleted || {};
      toast.success(
        `Cleared "${label}" (${d.pushes || 0} pushes, ${d.callback_events || 0} callbacks removed).`,
      );
      await fetchVoiceDrop();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear workflow history");
    } finally {
      setDeletingWf(null);
    }
  };

  const fetchInstantly = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/integrations/instantly/analytics`, {
        headers: getAuthHeaders(),
        timeout: 30000,
      });
      setEmailData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Instantly analytics");
      setEmailData(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchVoiceDrop = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/integrations/voice-drop/analytics`, {
        headers: getAuthHeaders(),
        timeout: 30000,
      });
      setVoiceData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Voice Drop analytics");
      setVoiceData(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const refresh = () => {
    if (view === "instantly") fetchInstantly();
    else fetchVoiceDrop();
  };

  useEffect(() => {
    if (view === "instantly") fetchInstantly();
    else fetchVoiceDrop();
  }, [view, fetchInstantly, fetchVoiceDrop]);

  const downloadWorkflowLeads = async (wf) => {
    setDownloadingWf(wf.workflow_id);
    try {
      const res = await axios.get(
        `${API}/integrations/voice-drop/workflow/${wf.workflow_id}/leads-csv`,
        { headers: getAuthHeaders(), responseType: "blob", timeout: 60000 }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (wf.workflow_name || "voice-drop").replace(/[^A-Za-z0-9_-]+/g, "_");
      link.download = `${safeName}-leads.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${wf.contacts_enrolled} contacted leads`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download leads CSV");
    } finally {
      setDownloadingWf(null);
    }
  };

  const syncCallbacks = async () => {
    setSyncingCallbacks(true);
    try {
      const res = await axios.post(
        `${API}/integrations/voice-drop/sync-callbacks`,
        {},
        { headers: getAuthHeaders(), timeout: 30000 }
      );
      const queued = res.data.queued || 0;
      if (queued === 0) {
        toast.info("No pushed contacts to sync yet.");
      } else {
        toast.success(res.data.message || `Syncing ${queued} contacts from GHL…`);
        // Poll analytics after ~30s so the fresh counts show up
        setTimeout(() => fetchVoiceDrop(), 30000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync callbacks from GHL");
    } finally {
      setSyncingCallbacks(false);
    }
  };

  const downloadInstantlyCampaignLeads = async (c) => {
    setDownloadingWf(c.id);
    try {
      const res = await axios.get(
        `${API}/integrations/instantly/campaign/${c.id}/leads-csv`,
        { headers: getAuthHeaders(), responseType: "blob", timeout: 60000 }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (c.name || "instantly-campaign").replace(/[^A-Za-z0-9_-]+/g, "_");
      link.download = `${safeName}-leads.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded campaign leads CSV`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download leads CSV");
    } finally {
      setDownloadingWf(null);
    }
  };

  const downloadFullReport = async () => {
    setDownloadingReport(true);
    try {
      const endpoint = view === "instantly"
        ? `${API}/integrations/instantly/analytics-csv`
        : `${API}/integrations/voice-drop/analytics-csv`;
      const res = await axios.get(endpoint, {
        headers: getAuthHeaders(),
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      link.download = `${view === "instantly" ? "instantly" : "voice-drop"}-report-${ts}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download report");
    } finally {
      setDownloadingReport(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Campaign Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">
            {view === "instantly"
              ? "Instantly email campaign performance"
              : "GoHighLevel Voice Drop workflow performance"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="inline-flex rounded-lg bg-white/70 border border-slate-200 p-1">
            <button
              onClick={() => setView("instantly")}
              data-testid="analytics-toggle-instantly"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                view === "instantly"
                  ? "bg-[#006994]/15 text-[#006994]"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <EnvelopeOpen size={14} weight="bold" />
              Instantly
            </button>
            <button
              onClick={() => setView("voice_drop")}
              data-testid="analytics-toggle-voice-drop"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                view === "voice_drop"
                  ? "bg-[#006994]/15 text-[#006994]"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Phone size={14} weight="bold" />
              Voice Drop
            </button>
          </div>
          <button
            onClick={downloadFullReport}
            disabled={downloadingReport || loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#006994]/10 text-[#006994] hover:bg-[#006994]/20 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="download-report-btn"
            title={`Download the full ${view === "instantly" ? "Instantly" : "Voice Drop"} report as CSV`}
          >
            {downloadingReport ? (
              <Spinner size={16} className="animate-spin" />
            ) : (
              <Download size={16} weight="bold" />
            )}
            Download Report
          </button>
          <button
            onClick={refresh}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-zinc-700 transition-colors text-sm"
            data-testid="refresh-analytics-btn"
          >
            <ArrowClockwise size={16} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size={32} className="animate-spin text-[#006994]" />
        </div>
      ) : view === "instantly" ? (
        <InstantlyAnalytics
          data={emailData}
          onDownload={downloadInstantlyCampaignLeads}
          downloadingWf={downloadingWf}
        />
      ) : (
        <VoiceDropAnalytics
          data={voiceData}
          onDownload={downloadWorkflowLeads}
          downloadingWf={downloadingWf}
          onSyncCallbacks={syncCallbacks}
          syncingCallbacks={syncingCallbacks}
          getAuthHeaders={getAuthHeaders}
          onDelete={deleteWorkflowHistory}
          deletingWf={deletingWf}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </div>
  );
};

/* ---------------- Instantly view ---------------- */
const InstantlyAnalytics = ({ data, onDownload, downloadingWf }) => {
  if (!data) {
    return (
      <div className="text-center py-20 text-slate-500" data-testid="instantly-empty">
        <p>Failed to load analytics. Check Instantly API key in Settings.</p>
      </div>
    );
  }
  const { summary, campaigns } = data;

  const statCards = [
    { label: "Total Sent", value: formatNum(summary.total_sent), icon: PaperPlaneTilt, color: "#F59E0B", bg: "rgba(245,158,11,0.1)" },
    { label: "Open Rate", value: `${summary.open_rate}%`, sub: `${formatNum(summary.total_opened)} opened`, icon: EnvelopeOpen, color: "#006994", bg: "rgba(59,130,246,0.1)" },
    { label: "Click Rate", value: `${summary.click_rate}%`, sub: `${formatNum(summary.total_clicked)} clicked`, icon: CursorClick, color: "#8B5CF6", bg: "rgba(139,92,246,0.1)" },
    { label: "Reply Rate", value: `${summary.reply_rate}%`, sub: `${formatNum(summary.total_replied)} replied`, icon: ChatCircle, color: "#EC4899", bg: "rgba(236,72,153,0.1)" },
    { label: "Opportunities", value: formatNum(summary.total_opportunities || 0), sub: `${summary.total_campaigns} campaigns`, icon: Lightning, color: "#10B981", bg: "rgba(16,185,129,0.1)" },
  ];

  return (
    <div className="space-y-6" data-testid="instantly-analytics">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      <InstantlyCharts summary={summary} campaigns={campaigns} />

      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-slate-900">Campaign Analytics</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[20%]">Campaign</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[9%]">Status</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[11%]">Seq. Started</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[11%]">Opened</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[11%]">Replied</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[9%]">Bounced</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[11%]">Opportunities</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[9%]">Report</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length > 0 ? campaigns.map((c) => (
                  <tr key={c.id} className="border-b border-slate-200 hover:bg-slate-100/20 transition-colors" data-testid={`instantly-campaign-row-${c.id}`}>
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                      <p className="text-xs text-slate-500">{formatNum(c.leads_count)} leads</p>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-slate-700 font-medium">{formatNum(c.contacted || c.sent)}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-sm text-slate-700">{formatNum(c.opened)}</span>
                      {c.open_rate > 0 && <span className="text-xs text-blue-400 ml-1">{c.open_rate}%</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-sm text-slate-700">{formatNum(c.replied)}</span>
                      {c.reply_rate > 0 && <span className="text-xs text-pink-400 ml-1">{c.reply_rate}%</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-sm text-slate-700">{formatNum(c.bounced)}</span>
                      {c.bounce_rate > 0 && <span className="text-xs text-red-400 ml-1">{c.bounce_rate}%</span>}
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-emerald-400 font-medium">{formatNum(c.opportunities || 0)}</td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => onDownload && onDownload(c)}
                        disabled={downloadingWf === c.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#006994]/10 text-[#006994] hover:bg-[#006994]/20 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                        data-testid={`instantly-download-${c.id}`}
                        title="Download contacted leads CSV for this campaign"
                      >
                        {downloadingWf === c.id ? (
                          <Spinner size={12} className="animate-spin" />
                        ) : (
                          <Download size={12} weight="bold" />
                        )}
                        CSV
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-slate-500">No campaigns found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------------- Voice Drop view ---------------- */
const VoiceDropAnalytics = ({ data, onDownload, downloadingWf, onSyncCallbacks, syncingCallbacks, getAuthHeaders, onDelete, deletingWf, isSuperAdmin }) => {
  const [expandedWf, setExpandedWf] = useState(null);
  if (!data) {
    return (
      <div className="text-center py-20 text-slate-500" data-testid="voice-drop-empty">
        <p>Failed to load Voice Drop analytics. Make sure GoHighLevel is configured for this organization.</p>
      </div>
    );
  }
  const { summary, workflows } = data;

  // Callback rate = total inbound calls received across all campaigns divided
  // by total contacts enrolled. Rounded to 1dp for readability.
  const totalCallbacks = summary.total_callbacks || 0;
  const callbackRate = summary.total_contacts_enrolled > 0
    ? ((totalCallbacks / summary.total_contacts_enrolled) * 100).toFixed(1)
    : "0.0";

  const statCards = [
    { label: "Workflows Active", value: formatNum(summary.total_workflows), icon: Broadcast, color: "#006994", bg: "rgba(0, 105, 148,0.1)" },
    { label: "Contacts Enrolled", value: formatNum(summary.total_contacts_enrolled), sub: `${formatNum(summary.total_pushed)} total pushes`, icon: UsersThree, color: "#10B981", bg: "rgba(16,185,129,0.1)" },
    { label: "Callbacks Received", value: formatNum(totalCallbacks), sub: `${callbackRate}% callback rate`, icon: Phone, color: "#F59E0B", bg: "rgba(245,158,11,0.1)" },
    { label: "Total Push Runs", value: formatNum(summary.total_pushes), sub: `${formatNum(summary.total_skipped)} already enrolled`, icon: PaperPlaneTilt, color: "#8B5CF6", bg: "rgba(139,92,246,0.1)" },
    { label: "Failed Enrollments", value: formatNum(summary.total_failed), icon: WarningCircle, color: "#EF4444", bg: "rgba(239,68,68,0.1)" },
  ];

  return (
    <div className="space-y-6" data-testid="voice-drop-analytics">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statCards.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      <VoiceDropCharts summary={summary} workflows={workflows} getAuthHeaders={getAuthHeaders} />

      {/* Approvals queue is a super-admin-only concern. Regular admins should
          feel their Voice Drop push started immediately — the approval gate
          is invisible to them and is handled behind the scenes. */}
      {isSuperAdmin && (
        <PendingApprovalsPanel getAuthHeaders={getAuthHeaders} onChange={onSyncCallbacks} />
      )}

      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg text-slate-900">Voice Drop Workflows</CardTitle>
          <button
            onClick={onSyncCallbacks}
            disabled={syncingCallbacks}
            data-testid="voice-drop-sync-callbacks-btn"
            className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Fetches inbound-call counts from GHL for every contact we pushed. Runs in the background — refresh in ~1 min for large campaigns."
          >
            {syncingCallbacks ? <Spinner size={14} className="animate-spin" /> : <ArrowClockwise size={14} />}
            {syncingCallbacks ? "Syncing…" : "Sync callbacks from GHL"}
          </button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[22%]">Workflow / Campaign</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[11%]">Contacts Enrolled</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[11%]">Callbacks</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[9%]">Pushed</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[9%]">Failed</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[10%]" title="Leads not re-pushed because they were already enrolled in this workflow.">Already Enrolled</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[8%]">Jobs</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[11%]">Last Push</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[10%]">Report</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase w-[6%]">Clear</th>
                </tr>
              </thead>
              <tbody>
                {workflows.length > 0 ? workflows.map((w) => {
                  const expanded = expandedWf === w.workflow_id;
                  const failures = w.recent_failures || [];
                  const canExpand = failures.length > 0;
                  return (
                  <Fragment key={w.workflow_id}>
                  <tr className="border-b border-slate-200 hover:bg-slate-100/20 transition-colors" data-testid={`voice-drop-row-${w.workflow_id}`}>
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-slate-900 truncate">{w.workflow_name}</p>
                      <p className="text-xs text-slate-500 font-mono truncate">{w.workflow_id}</p>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-sm text-emerald-400 font-semibold">{formatNum(w.contacts_enrolled)}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className={`text-sm font-semibold ${w.callbacks_received > 0 ? "text-amber-700" : "text-slate-400"}`}>
                          {formatNum(w.callbacks_received || 0)}
                        </span>
                        {w.contacts_enrolled > 0 && w.callbacks_received > 0 ? (
                          <span className="text-[10px] text-slate-500">
                            {((w.callbacks_received / w.contacts_enrolled) * 100).toFixed(1)}%
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-slate-700">{formatNum(w.total_pushed)}</td>
                    <td className="py-3 px-4 text-center">
                      {canExpand ? (
                        <button
                          onClick={() => setExpandedWf(expanded ? null : w.workflow_id)}
                          className={`text-sm underline decoration-dotted hover:no-underline ${w.total_failed > 0 ? "text-red-400" : "text-slate-500"}`}
                          data-testid={`voice-drop-show-failures-${w.workflow_id}`}
                          title="Click to see why these failed"
                        >
                          {formatNum(w.total_failed)}
                        </button>
                      ) : (
                        <span className={`text-sm ${w.total_failed > 0 ? "text-red-400" : "text-slate-500"}`}>{formatNum(w.total_failed)}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-slate-500" title="Leads not re-pushed because they were already enrolled in this workflow.">{formatNum(w.total_skipped)}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-slate-700">
                        <Briefcase size={12} /> {formatNum(w.jobs_count)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-slate-500">{formatDate(w.last_push_at)}</td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => onDownload(w)}
                        disabled={!w.contacts_enrolled || downloadingWf === w.workflow_id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#006994]/10 text-[#006994] hover:bg-[#006994]/20 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                        data-testid={`voice-drop-download-${w.workflow_id}`}
                        title={w.contacts_enrolled ? "Download contacted leads CSV" : "No leads to download"}
                      >
                        {downloadingWf === w.workflow_id ? (
                          <Spinner size={12} className="animate-spin" />
                        ) : (
                          <Download size={12} weight="bold" />
                        )}
                        CSV
                      </button>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => onDelete && onDelete(w)}
                        disabled={deletingWf === w.workflow_id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid={`voice-drop-delete-${w.workflow_id}`}
                        title="Remove this workflow's history from Dolphin LeadFlow (GHL workflow itself is untouched)"
                      >
                        {deletingWf === w.workflow_id ? (
                          <Spinner size={14} className="animate-spin" />
                        ) : (
                          <Trash size={14} weight="bold" />
                        )}
                      </button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-red-950/10 border-b border-slate-200" data-testid={`voice-drop-failures-${w.workflow_id}`}>
                      <td colSpan={10} className="py-3 px-4">
                        <div className="text-xs uppercase tracking-wider text-red-400/80 mb-2 font-semibold">
                          Recent enrollment failures ({failures.length} of {w.total_failed})
                        </div>
                        <div className="space-y-2">
                          {failures.map((f, i) => (
                            <div key={i} className="flex items-start gap-3 text-xs">
                              <WarningCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" weight="bold" />
                              <div className="flex-1 min-w-0">
                                <div className="text-slate-700">
                                  <span className="font-medium">{f.business_name || "—"}</span>
                                  {f.phone && <span className="text-slate-500 ml-2">{f.phone}</span>}
                                  {f.email && <span className="text-slate-500 ml-2">{f.email}</span>}
                                </div>
                                <div className="text-red-300/90 mt-0.5 break-words">{f.error}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                }) : (
                  <tr>
                    <td colSpan={10} className="py-12 text-center text-slate-500" data-testid="voice-drop-no-data">
                      No voice drop pushes yet. Push a phone-enriched job to a GHL workflow from the Job Details page to populate this view.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------------- Charts ---------------- */

const TOOLTIP_STYLE = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  color: "#0F172A",
  fontSize: 12,
  padding: "8px 12px",
  boxShadow: "0 4px 12px 0 rgba(15, 23, 42, 0.12)",
};

// Custom tooltip — Recharts default ties item color to the bar's fill which
// stays legible against our white surface as long as we set explicit slate
// text colors (label + values) here.
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={TOOLTIP_STYLE}>
      {label !== undefined && label !== "" && (
        <div className="text-slate-900 font-medium mb-1">{label}</div>
      )}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
            style={{ backgroundColor: p.color || p.fill || "#fafafa" }}
          />
          <span className="text-slate-700">{p.name === "value" ? "Count" : p.name}:</span>
          <span className="text-slate-900 font-semibold">{formatNum(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const InstantlyCharts = ({ summary, campaigns }) => {
  const funnelData = useMemo(() => ([
    { stage: "Sent", value: summary.total_sent || 0, fill: "#F59E0B" },
    { stage: "Opened", value: summary.total_opened || 0, fill: "#006994" },
    { stage: "Replied", value: summary.total_replied || 0, fill: "#EC4899" },
    { stage: "Clicked", value: summary.total_clicked || 0, fill: "#8B5CF6" },
    { stage: "Opportunities", value: summary.total_opportunities || 0, fill: "#10B981" },
  ]), [summary]);

  const topCampaigns = useMemo(() => {
    return [...(campaigns || [])]
      .sort((a, b) => (b.replied || 0) - (a.replied || 0))
      .slice(0, 5)
      .map((c) => ({
        name: (c.name || "").length > 24 ? `${c.name.slice(0, 24)}…` : c.name || "—",
        Replied: c.replied || 0,
        Opened: c.opened || 0,
        Opps: c.opportunities || 0,
      }));
  }, [campaigns]);

  const hasAnyData = funnelData.some((d) => d.value > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="instantly-charts">
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-900">Engagement Funnel</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          {hasAnyData ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={funnelData} layout="vertical" margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" stroke="#71717A" fontSize={11} tickFormatter={(v) => formatNum(v)} />
                <YAxis type="category" dataKey="stage" stroke="#A1A1AA" fontSize={12} width={90} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(15,23,42,0.04)" }} />
                <Bar dataKey="value" name="Count" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="No engagement data yet" />
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-900">Top Campaigns by Replies</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          {topCampaigns.length > 0 && topCampaigns.some((c) => c.Replied + c.Opened > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topCampaigns} margin={{ top: 10, right: 12, left: 0, bottom: 70 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="name"
                  stroke="#71717A"
                  fontSize={10}
                  angle={-25}
                  textAnchor="end"
                  height={70}
                  interval={0}
                  tick={{ fill: "#A1A1AA" }}
                />
                <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => formatNum(v)} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(15,23,42,0.04)" }} />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: "#A1A1AA", paddingBottom: 8 }}
                  iconSize={10}
                  verticalAlign="top"
                  align="right"
                />
                <Bar dataKey="Opened" fill="#006994" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Replied" fill="#EC4899" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Opps" name="Opps" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="No campaign engagement yet" />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PendingApprovalsPanel = ({ getAuthHeaders, onChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/integrations/voice-drop/pending-approvals`,
        { headers: getAuthHeaders() },
      );
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load pending approvals");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPending();
    // Auto-refresh so the queue stays live for other admins reviewing
    // concurrently — cheap query, only pending rows are returned.
    const t = setInterval(fetchPending, 30000);
    return () => clearInterval(t);
  }, [fetchPending]);

  const approve = async (row) => {
    if (!window.confirm(
      `Approve this Voice Drop push?\n\n` +
      `Caller-ID: ${row.phone_number}\n` +
      `Workflow: ${row.workflow_id}\n` +
      `Contacts: ${row.to_push}\n\n` +
      `Have you already set the workflow's From Number in GHL to ${row.phone_number}?`,
    )) return;
    setActioningId(row.id);
    try {
      await axios.post(
        `${API}/integrations/voice-drop/pushes/${row.id}/approve`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(`Approved — GHL push starting now (${row.to_push} contacts).`);
      await fetchPending();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve push");
    } finally {
      setActioningId(null);
    }
  };

  const reject = async (row) => {
    const reason = window.prompt(`Reject this Voice Drop push? (Optional reason will be logged)`, "");
    if (reason === null) return;
    setActioningId(row.id);
    try {
      await axios.post(
        `${API}/integrations/voice-drop/pushes/${row.id}/reject`,
        { reason },
        { headers: getAuthHeaders() },
      );
      toast.success("Rejected.");
      await fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject push");
    } finally {
      setActioningId(null);
    }
  };

  if (loading && !rows.length) return null;
  if (!rows.length) return null;

  return (
    <Card className="bg-amber-50 border-amber-300" data-testid="pending-approvals-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-amber-900 flex items-center gap-2">
          <WarningCircle size={18} weight="bold" />
          Pending Voice Drop Approvals ({rows.length})
        </CardTitle>
        <p className="text-xs text-amber-800 mt-1">
          Before approving, set each workflow&apos;s <strong>From Number</strong> in GHL → Workflow Settings to match the caller-ID below.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-amber-200 bg-amber-100/50">
                <th className="text-left py-2 px-4 text-xs font-medium text-amber-900 uppercase">Workflow</th>
                <th className="text-left py-2 px-4 text-xs font-medium text-amber-900 uppercase">Caller-ID (set in GHL)</th>
                <th className="text-left py-2 px-4 text-xs font-medium text-amber-900 uppercase">Owner</th>
                <th className="text-left py-2 px-4 text-xs font-medium text-amber-900 uppercase">Job</th>
                <th className="text-center py-2 px-4 text-xs font-medium text-amber-900 uppercase">Contacts</th>
                <th className="text-left py-2 px-4 text-xs font-medium text-amber-900 uppercase">Requested By</th>
                <th className="text-right py-2 px-4 text-xs font-medium text-amber-900 uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-amber-200 hover:bg-amber-100/30" data-testid={`pending-row-${r.id}`}>
                  <td className="py-3 px-4 text-slate-900">
                    <div className="text-xs font-mono text-slate-500 truncate max-w-[180px]">{r.workflow_id}</div>
                    <div className="text-[11px] text-slate-500">{r.organization_name || ""}</div>
                  </td>
                  <td className="py-3 px-4 font-mono text-cyan-800 font-semibold">{r.phone_number || "—"}</td>
                  <td className="py-3 px-4 text-slate-700">{r.owner_name || <span className="text-slate-400 font-mono text-xs">{r.assigned_user_id?.slice(0, 8)}</span>}</td>
                  <td className="py-3 px-4 text-slate-700 truncate max-w-[180px]">{r.job_name || r.job_id?.slice(0, 8)}</td>
                  <td className="py-3 px-4 text-center text-slate-900 font-semibold">{r.to_push}</td>
                  <td className="py-3 px-4 text-slate-600 text-xs">{r.pushed_by}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => approve(r)}
                        disabled={actioningId === r.id}
                        className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`approve-push-${r.id}`}
                      >
                        {actioningId === r.id ? "…" : "Approve"}
                      </button>
                      <button
                        onClick={() => reject(r)}
                        disabled={actioningId === r.id}
                        className="px-3 py-1.5 rounded-md bg-white hover:bg-red-50 text-red-700 border border-red-300 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`reject-push-${r.id}`}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};


const VoiceDropCharts = ({ summary, workflows, getAuthHeaders }) => {
  const [bucket, setBucket] = useState("day");
  const [seriesData, setSeriesData] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setSeriesLoading(true);
      try {
        const res = await axios.get(
          `${API}/integrations/voice-drop/callbacks-timeseries?bucket=${bucket}`,
          { headers: getAuthHeaders(), timeout: 20000 }
        );
        if (!ignore) setSeriesData(res.data);
      } catch (err) {
        if (!ignore) setSeriesData(null);
      } finally {
        if (!ignore) setSeriesLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [bucket, getAuthHeaders]);

  // Format the bucket start timestamp for the chart X-axis (compact) and
  // tooltip (verbose) — matches the granularity the user selected.
  const fmtBucketShort = (iso) => {
    try {
      const d = new Date(iso);
      if (bucket === "hour") return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", hour12: true });
      if (bucket === "month") return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch { return iso; }
  };
  const fmtBucketLong = (iso) => {
    try {
      const d = new Date(iso);
      if (bucket === "hour") return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
      if (bucket === "week") {
        const end = new Date(d.getTime() + 6 * 24 * 60 * 60 * 1000);
        return `Week of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
      }
      if (bucket === "month") return d.toLocaleString("en-US", { month: "long", year: "numeric" });
      return d.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
    } catch { return iso; }
  };

  const chartRows = useMemo(() => {
    const raw = (seriesData?.series || []).map((p) => ({
      t: p.t,
      label: fmtBucketShort(p.t),
      tooltip: fmtBucketLong(p.t),
      Callbacks: p.count,
    }));
    return raw;
  }, [seriesData, bucket]);

  const topWorkflows = useMemo(() => {
    return [...(workflows || [])]
      .sort((a, b) => (b.contacts_enrolled || 0) - (a.contacts_enrolled || 0))
      .slice(0, 5)
      .map((w) => ({
        name: (w.workflow_name || "").length > 22 ? `${w.workflow_name.slice(0, 22)}…` : w.workflow_name || "—",
        Enrolled: w.contacts_enrolled || 0,
        Callbacks: w.callbacks_received || 0,
      }));
  }, [workflows]);

  const bucketButtons = [
    { key: "hour", label: "Hourly" },
    { key: "day", label: "Daily" },
    { key: "week", label: "Weekly" },
    { key: "month", label: "Monthly" },
  ];

  const totalCallbacks = summary.total_callbacks || 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="voice-drop-charts">
      {/* Callbacks Over Time — replaces the confusing "Push Outcomes" pie */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base text-slate-900">Callbacks Over Time</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">{formatNum(totalCallbacks)} total callbacks — synced from GHL</p>
          </div>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="callback-bucket-toggle">
            {bucketButtons.map((b) => (
              <button
                key={b.key}
                onClick={() => setBucket(b.key)}
                data-testid={`callback-bucket-${b.key}`}
                className={`px-2.5 py-1 text-xs transition-colors ${bucket === b.key ? "bg-amber-500 text-white font-semibold" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {seriesLoading ? (
            <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">
              <Spinner size={18} className="animate-spin mr-2" /> Loading callback timeline…
            </div>
          ) : chartRows.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartRows} margin={{ top: 10, right: 12, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="label"
                  stroke="#71717A"
                  fontSize={10}
                  tick={{ fill: "#A1A1AA" }}
                  interval="preserveStartEnd"
                />
                <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} tickFormatter={(v) => formatNum(v)} />
                <Tooltip content={<CallbackTooltip />} cursor={{ fill: "rgba(245,158,11,0.08)" }} />
                <Bar dataKey="Callbacks" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label={totalCallbacks === 0 ? 'No callbacks yet. Click "Sync callbacks from GHL" to fetch the latest inbound-call data.' : "No calls in this time range"} />
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-900">Top Workflows — Enrolled vs Callbacks</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          {topWorkflows.length > 0 && topWorkflows.some((w) => w.Enrolled > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topWorkflows} margin={{ top: 10, right: 12, left: 0, bottom: 70 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="name"
                  stroke="#71717A"
                  fontSize={10}
                  angle={-25}
                  textAnchor="end"
                  height={70}
                  interval={0}
                  tick={{ fill: "#A1A1AA" }}
                />
                <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => formatNum(v)} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(15,23,42,0.04)" }} />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: "#A1A1AA", paddingBottom: 8 }}
                  iconSize={10}
                  verticalAlign="top"
                  align="right"
                />
                <Bar dataKey="Enrolled" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Callbacks" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="No workflows pushed to yet" />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Dedicated tooltip so the date label is verbose ("Wed, Feb 3, 2026")
// and the callback count is highlighted in amber.
const CallbackTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload || {};
  return (
      <div className="bg-white border border-slate-200 rounded px-3 py-2 shadow-lg">
        <div className="text-[11px] text-slate-500">{row.tooltip}</div>
        <div className="text-sm mt-1">
          <span className="text-[#006994] font-semibold">{formatNum(row.Callbacks)}</span>{" "}
          <span className="text-slate-500">callback{row.Callbacks === 1 ? "" : "s"}</span>
        </div>
      </div>
  );
};

const EmptyChart = ({ label }) => (
  <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm" data-testid="empty-chart">
    {label}
  </div>
);

/* ---------------- Helpers ---------------- */
const StatCard = ({ label, value, sub, icon: Icon, color, bg }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: bg }}>
          <Icon size={18} style={{ color }} weight="bold" />
        </div>
        <span className="text-xs text-slate-500 font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "1") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <Play size={10} weight="fill" /> Active
      </span>
    );
  }
  if (s === "paused" || s === "0") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-500/40">
        <Pause size={10} weight="fill" /> Paused
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
      <Circle size={10} /> {status || "Unknown"}
    </span>
  );
};

function formatNum(n) {
  // Show exact counts with thousands separators. Users need precision when
  // reconciling push counts, GHL enrollment totals, and campaign performance —
  // rounded abbreviations like "4.8K" hid the difference between 4,778 and
  // 4,999 which broke debugging when leads were "missing".
  if (n === null || n === undefined) return "0";
  const num = Number(n) || 0;
  return num.toLocaleString("en-US");
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default AnalyticsPage;
